import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, TextField, Typography, Container, Box } from "@mui/material";
import { useSnackbar } from "../../context/SnackbarContext";
import { useConfirmPasswordReset } from "../../api/user/use-confirm-password-reset";
import { useFormik } from "formik";
import * as Yup from "yup";

const passwordRules =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|`~]).{8,}$/;

const validationSchema = Yup.object({
  password: Yup.string()
    .required("Password is required")
    .matches(
      passwordRules,
      "Min 8 chars, at least 1 uppercase, 1 lowercase, 1 number, 1 special character"
    ),
  confirm: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password"), null], "Passwords must match"),
});

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutate, isPending } = useConfirmPasswordReset();

  const [done, setDone] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirm: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (!token) return;
      mutate(
        { token, password: values.password },
        {
          onSuccess: () => {
            setDone(true);
            showSnackbar("Password successfully reset!", "success");
            setTimeout(() => navigate("/signin"), 2000);
          },
          onError: () =>
            formik.setErrors({
              confirm:
                "Failed to reset password. Token may be invalid or expired.",
            }),
        }
      );
    },
  });

  if (!token)
    return (
      <Container maxWidth="xs" sx={{ mt: 8 }}>
        <Typography color="error">Invalid password reset link</Typography>
      </Container>
    );
  if (done)
    return (
      <Container maxWidth="xs" sx={{ mt: 8 }}>
        <Typography>Password reset! Redirecting to sign in…</Typography>
      </Container>
    );

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          mt: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography component="h1" variant="h5">
          Reset Password
        </Typography>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{ mt: 2 }}
          noValidate
        >
          <TextField
            fullWidth
            required
            label="New Password"
            margin="normal"
            type="password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={
              formik.touched.password && formik.errors.password
                ? formik.errors.password
                : "Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char"
            }
          />
          <TextField
            fullWidth
            required
            label="Confirm Password"
            margin="normal"
            type="password"
            name="confirm"
            value={formik.values.confirm}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirm && Boolean(formik.errors.confirm)}
            helperText={
              formik.touched.confirm && formik.errors.confirm
                ? formik.errors.confirm
                : ""
            }
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            disabled={isPending}
          >
            Set New Password
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
